import { useMemo } from "react";
import { TileLayer } from "react-leaflet";
import type { LeafletEventHandlerFnMap, TileEvent } from "leaflet";
import { useTileCoverageTracker } from "@/features/map/hooks/useTileCoverageTracker";
import type { TileCoverageStatus } from "@/features/map/utils/tileCoverage";

const disablePlusLighterBlend = ({ tile }: TileEvent): void => {
  // Leaflet 1.9.4 の plus-lighter は、隣接タイルがサブピクセルで重なる箇所を
  // 加算合成して明るい線にする。写真タイルだけ通常合成へ戻して境界線を防ぐ。
  tile.style.mixBlendMode = "normal";
};

export interface GsiPhotoTileLayerProps {
  /** レイヤーコントロールでの表示ON/OFF */
  visible: boolean;
  /** 地理院タイルのURLテンプレート */
  url: string;
  /** 表示を開始するズームレベル */
  minZoom: number;
  /** 地理院が配信する最大ズームレベル（これを超える範囲は拡大表示で補う） */
  maxNativeZoom: number;
  /** 表示を終了するズームレベル */
  maxZoom: number;
  zIndex: number;
  /** Leaflet の attribution コントロールに表示する出典（HTML可） */
  attribution?: string;
  /** 透過率（0〜1）。将来の透過率設定 UI から渡す想定。未指定なら不透明。 */
  opacity?: number;
  className?: string;
  /**
   * 現在の表示範囲にデータがあるかの判定結果を受け取る。
   * データがない範囲は 404 で返るため、タイルの読み込み結果から判定する。
   * null は「非表示になった／アンマウントされた」の意味で、表示中の通知を消すために使う。
   */
  onCoverageChange?: (status: TileCoverageStatus | null) => void;
}

/**
 * 国土地理院の写真系ラスタタイル（空中写真・衛星画像）を描画する共通レイヤー。
 *
 * 全国最新写真・年度別空中写真・年代別の写真はすべてこのコンポーネントを使い、
 * 写真タイル特有の描画対策（合成モード）を一か所に集約する。
 */
export const GsiPhotoTileLayer: React.FC<GsiPhotoTileLayerProps> = ({
  visible,
  url,
  minZoom,
  maxNativeZoom,
  maxZoom,
  zIndex,
  attribution,
  opacity = 1,
  className,
  onCoverageChange,
}) => {
  const coverageHandlers = useTileCoverageTracker({
    enabled: visible,
    minZoom,
    maxZoom,
    onCoverageChange,
  });
  const eventHandlers = useMemo<LeafletEventHandlerFnMap>(
    () => ({
      ...coverageHandlers,
      tileloadstart: (event) => {
        disablePlusLighterBlend(event);
        coverageHandlers.tileloadstart?.(event);
      },
    }),
    [coverageHandlers]
  );

  if (!visible) {
    return null;
  }

  return (
    <TileLayer
      className={className}
      attribution={attribution}
      eventHandlers={eventHandlers}
      url={url}
      minZoom={minZoom}
      maxNativeZoom={maxNativeZoom}
      maxZoom={maxZoom}
      zIndex={zIndex}
      opacity={opacity}
    />
  );
};
